import { z } from 'zod';
import { userRoles, type UserRole } from './enums.js';

/**
 * The sidebar's 9 destinations, and which roles are ever eligible to see
 * each one — the ceiling admin's per-role menu toggle (docs/spec.md §14
 * round 3, E8) can narrow within, but never exceed. This is the single
 * source of truth for both the web app's nav (apps/web/src/app/nav.ts) and
 * the API's menu-access service, so the two can't drift apart.
 *
 * Menu visibility is presentation only — the API's own `requireRole` checks
 * are what actually gate access (spec §6), and are unaffected by this list
 * or by any row an admin sets in `role_menu_access`.
 */
// Named (rather than inlined) so `as const` below doesn't narrow each array
// to its own literal-tuple type — that would make `.includes(role)` reject a
// plain `UserRole` argument everywhere except on the `ALL` ones.
const ALL: readonly UserRole[] = userRoles;
const ADMIN_DOCTOR_RECEPTION: readonly UserRole[] = ['admin', 'doctor', 'reception'];
const ADMIN_RECEPTION: readonly UserRole[] = ['admin', 'reception'];
const ADMIN_ONLY: readonly UserRole[] = ['admin'];

export const navRoleCeiling = {
  dashboard: ALL,
  appointments: ALL,
  patients: ALL,
  billing: ADMIN_DOCTOR_RECEPTION,
  inventory: ADMIN_DOCTOR_RECEPTION,
  medSale: ADMIN_RECEPTION,
  lab: ALL,
  revenue: ADMIN_ONLY,
  stockReport: ADMIN_ONLY,
  profitReport: ADMIN_ONLY,
  settings: ALL,
} as const satisfies Record<string, readonly UserRole[]>;

export const navKeys = Object.keys(navRoleCeiling) as NavKey[];
export type NavKey = keyof typeof navRoleCeiling;

export const navKeySchema = z.enum(navKeys as [NavKey, ...NavKey[]]);

export const menuAccessUpdateSchema = z.object({
  updates: z.array(
    z.object({
      navKey: navKeySchema,
      role: z.enum(userRoles),
      visible: z.boolean(),
    }),
  ),
});
export type MenuAccessUpdate = z.infer<typeof menuAccessUpdateSchema>;

/** One row per nav item; `roles` only lists roles within that item's ceiling. */
export interface MenuAccessRow {
  navKey: NavKey;
  roles: Partial<Record<UserRole, boolean>>;
}
